import { markRaw, reactive, toRaw } from "vue";
import {
  MAX_MESSAGE_ATTACHMENTS,
  MAX_MESSAGE_ATTACHMENT_MB,
  MAX_MESSAGE_REQUEST_BYTES,
  appendMessageAttachment,
  hasMessageAttachment,
  isAllowedAttachmentFile,
  isAttachmentWithinSizeLimit,
  isVideoLikeFile,
  isWithinMessageRequestLimit,
  messageAttachmentAccept as acceptFor,
  messageBodyWithOptionalAttachment,
  attachmentPreviewLabel,
  markAttachmentRemoved,
  normalizeMessageAttachments,
  unattachedUids,
  videoOffered,
} from "@/utils/messageAttachments";
import { isUploadBusy, isUploadInFlight, validateVideoFile } from "@/utils/videoUploadPlan";
import { canDeleteMessageAttachment } from "@/utils/fileDelete";
import { deleteMessageAttachment } from "@/api/keyonFiles";
import { loadVideoPolicy } from "@/api/videoUpload";
import { createVideoUploader } from "@/utils/videoUploader";

const MAX_MESSAGE_REQUEST_MB = Math.round(MAX_MESSAGE_REQUEST_BYTES / (1024 * 1024));

// A send that came back "Inserted" without the video on the message: an older
// keyon replica (or an image rolled back) ignores video_file_uids, saves the
// message and answers 200. The message did go out; the video did not.
export const VIDEO_NOT_ATTACHED_TEXT =
  "The video was not attached, so the message went out without it. Your video is still uploaded: press Send to send it again.";
export const VIDEO_UPLOADING_TEXT =
  "A video is still uploading. Stay on this screen until it finishes, or cancel the upload first.";

// A failed policy call is asked again at the next opportunity, but not at every one.
const POLICY_RETRY_MS = 30000;

// The three boxes a person can attach to, by the name the send code uses for each.
const LISTS = {
  compose: "composeAttachmentFiles",
  quickReply: "quickReplyAttachmentFiles",
  reply: "replyAttachmentFiles",
};

/** A video in a list: an object that stands where a File would, and carries its upload. */
const isVideoEntry = (item) => !!item && item.isVideoEntry === true;

// What the browser says when the page is closed or left mid-upload.
function warnBeforeUnload(event) {
  event.preventDefault();
  event.returnValue = "";
  return "";
}

/**
 * Shared multi-attachment pick/send helpers for Floating + Mobile widgets.
 * Also hosts replySubject() so both widgets keep a single "Re: " prefix.
 */
export default {
  data() {
    return {
      composeAttachmentFiles: [],
      quickReplyAttachmentFiles: [],
      replyAttachmentFiles: [],
      // The attachment its sender is removing: { fileUid, name }, or null.
      removeAttachmentTarget: null,
      removeAttachmentBusy: false,
      removeAttachmentError: "",
      // GET video_upload.php?action=config, fetched when the attach picker is
      // first wanted (never on mount): null until it is, and after a failure,
      // and then the picker and its checks are exactly what they were before video.
      videoPolicy: null,
      videoPolicyState: "idle", // idle | loading | loaded | failed
      videoPolicyFailedAt: 0,
      // One send at a time per box: a second Enter while a request is out would send twice.
      sendLocks: { compose: false, quickReply: false, reply: false },
      videoUnloadHandler: null,
    };
  },
  computed: {
    messageAttachmentAccept() {
      return acceptFor(this.videoPolicy);
    },
    maxMessageAttachments() {
      return MAX_MESSAGE_ATTACHMENTS;
    },
    // Reads the entries' states, so it changes as an upload does.
    anyVideoUploadInFlight() {
      return this.videoEntries().some((entry) => isUploadInFlight(entry.upload.status));
    },
  },
  watch: {
    // The beforeunload prompt is armed only while a video is going up.
    anyVideoUploadInFlight(active) {
      this.armUnloadGuard(active);
    },
    // Both widgets have an activeView. The policy is wanted by the time a box
    // that can attach is on screen, so the first pick already offers video.
    activeView(view) {
      if (view === "compose" || view === "thread") this.ensureVideoPolicy();
    },
  },
  beforeUnmount() {
    this.armUnloadGuard(false);
    this.discardAllVideoEntries();
  },
  methods: {
    hasMessageAttachment,
    attachmentPreviewLabel,
    normalizeMessageAttachments,

    /** SweetAlert when available; falls back to alert(). */
    rejectAttachment(title, text) {
      if (typeof this.$swal === "function") {
        this.$swal({
          type: "error",
          title,
          text,
        });
        return;
      }
      alert(text);
    },

    // The click is made at once, inside the person's gesture; the policy is asked
    // for in the background (a picker that waited for a request would be refused
    // by the browser, which only lets a click through a gesture).
    triggerComposeAttachmentPicker() {
      this.ensureVideoPolicy();
      this.$refs.composeAttachmentInput?.click();
    },
    triggerQuickReplyAttachmentPicker() {
      this.ensureVideoPolicy();
      this.$refs.quickReplyAttachmentInput?.click();
    },
    triggerReplyAttachmentPicker() {
      this.ensureVideoPolicy();
      this.$refs.replyAttachmentInput?.click();
    },

    /**
     * The video policy, once. A failure is remembered for 30 seconds, then asked
     * again at the next opportunity.
     */
    async ensureVideoPolicy() {
      if (this.videoPolicyState === "loading" || this.videoPolicyState === "loaded") return this.videoPolicy;
      if (this.videoPolicyState === "failed" && Date.now() - this.videoPolicyFailedAt < POLICY_RETRY_MS) {
        return this.videoPolicy;
      }
      this.videoPolicyState = "loading";
      let policy = null;
      try {
        policy = await loadVideoPolicy();
      } catch (_) {
        policy = null;
      }
      if (policy) {
        this.videoPolicy = policy;
        this.videoPolicyState = "loaded";
      } else {
        this.videoPolicyState = "failed";
        this.videoPolicyFailedAt = Date.now();
      }
      return this.videoPolicy;
    },

    // Copy FileList BEFORE resetting the input — FileList is live and clears with value="".
    snapshotSelectedFiles(event) {
      const list = Array.from(event?.target?.files || []);
      if (event?.target) event.target.value = "";
      return list;
    },

    /**
     * Merge newly picked files into the list.
     * Enforces max count, type, size (20MB), and dedupes by name+size. A video
     * (when the server takes one) is held to its own rules (validateVideoFile)
     * and becomes an entry that uploads at once; it takes one of the five places.
     */
    mergePickedFiles(currentList, incomingFiles) {
      const next = Array.isArray(currentList) ? [...currentList] : [];
      const videoOn = videoOffered(this.videoPolicy);
      for (const file of incomingFiles || []) {
        const raw = toRaw(file) || file;
        if (next.length >= MAX_MESSAGE_ATTACHMENTS) {
          this.rejectAttachment(
            "Too many files",
            `You can attach up to ${MAX_MESSAGE_ATTACHMENTS} files per message.`
          );
          break;
        }
        if (videoOn && isVideoLikeFile(raw)) {
          const verdict = validateVideoFile(raw, this.videoPolicy, {
            videosInMessage: next.filter(isVideoEntry).length,
          });
          if (!verdict.ok) {
            this.rejectAttachment("Video not accepted", verdict.message);
            continue;
          }
          if (next.some((f) => f.name === raw.name && f.size === raw.size)) continue;
          next.push(this.createVideoEntry(raw));
          continue;
        }
        if (!isAllowedAttachmentFile(raw, this.videoPolicy)) {
          this.rejectAttachment(
            "File type not allowed",
            videoOn
              ? `"${raw.name}" was skipped. Please choose an image, PDF, document (DOC, DOCX, XLS, XLSX, TXT, CSV), or an MP4 or MOV video.`
              : `"${raw.name}" was skipped. Please choose an image, PDF, or document (DOC, DOCX, XLS, XLSX, TXT, CSV).`
          );
          continue;
        }
        if (!isAttachmentWithinSizeLimit(raw, this.videoPolicy)) {
          this.rejectAttachment(
            "File too large",
            `"${raw.name}" exceeds the ${MAX_MESSAGE_ATTACHMENT_MB}MB limit. Please choose a smaller file.`
          );
          continue;
        }
        const dup = next.some((f) => f.name === raw.name && f.size === raw.size);
        if (dup) continue;
        // keyon cannot answer a request PHP cut off at post_max_size, so the
        // total is checked here rather than left to a failed send.
        if (!isWithinMessageRequestLimit([...next, raw])) {
          this.rejectAttachment(
            "Attachments too large",
            `"${raw.name}" was skipped. Attachments on one message can add up to ${MAX_MESSAGE_REQUEST_MB}MB in total.`
          );
          continue;
        }
        next.push(raw);
      }
      return next;
    },

    onComposeAttachmentSelected(event) {
      const files = this.snapshotSelectedFiles(event);
      if (!files.length) return;
      this.composeAttachmentFiles = this.mergePickedFiles(this.composeAttachmentFiles, files);
    },
    onQuickReplyAttachmentSelected(event) {
      const files = this.snapshotSelectedFiles(event);
      if (!files.length) return;
      this.quickReplyAttachmentFiles = this.mergePickedFiles(
        this.quickReplyAttachmentFiles,
        files
      );
    },
    onReplyAttachmentSelected(event) {
      const files = this.snapshotSelectedFiles(event);
      if (!files.length) return;
      this.replyAttachmentFiles = this.mergePickedFiles(this.replyAttachmentFiles, files);
    },

    removeComposeAttachmentAt(index) {
      this.releaseVideoEntry(this.composeAttachmentFiles.splice(index, 1)[0]);
    },
    removeQuickReplyAttachmentAt(index) {
      this.releaseVideoEntry(this.quickReplyAttachmentFiles.splice(index, 1)[0]);
    },
    removeReplyAttachmentAt(index) {
      this.releaseVideoEntry(this.replyAttachmentFiles.splice(index, 1)[0]);
    },

    clearComposeAttachment() {
      this.composeAttachmentFiles.forEach(this.releaseVideoEntry);
      this.composeAttachmentFiles = [];
    },
    clearQuickReplyAttachment() {
      this.quickReplyAttachmentFiles.forEach(this.releaseVideoEntry);
      this.quickReplyAttachmentFiles = [];
    },
    clearReplyAttachment() {
      this.replyAttachmentFiles.forEach(this.releaseVideoEntry);
      this.replyAttachmentFiles = [];
    },

    // Files go in files[]. A video never does: it went up in chunks before the
    // send and is named by uid, in video_file_uids (a JSON list, at most one).
    appendComposeAttachment(formData) {
      return this.appendWithVideo(formData, "compose");
    },
    appendQuickReplyAttachment(formData) {
      return this.appendWithVideo(formData, "quickReply");
    },
    appendReplyAttachment(formData) {
      return this.appendWithVideo(formData, "reply");
    },
    appendWithVideo(formData, listName) {
      appendMessageAttachment(formData, this[LISTS[listName]].filter((item) => !isVideoEntry(item)));
      const uids = this.videoUidsOf(listName);
      if (uids.length) formData.append("video_file_uids", JSON.stringify(uids));
      return formData;
    },

    /** Message body, or "(Attachment)" when sending files with no text. */
    composeMessageBody() {
      return messageBodyWithOptionalAttachment(
        this.newMessage.message,
        this.composeAttachmentFiles
      );
    },
    quickReplyMessageBody() {
      return messageBodyWithOptionalAttachment(
        this.quickReplyMessage,
        this.quickReplyAttachmentFiles
      );
    },
    replyMessageBody() {
      return messageBodyWithOptionalAttachment(this.replyMessage, this.replyAttachmentFiles);
    },

    clearAllAttachments() {
      this.clearComposeAttachment();
      this.clearQuickReplyAttachment();
      this.clearReplyAttachment();
    },

    /*
     * Videos. A picked video is an entry in the list that stands where a File
     * would (name, size, type) and carries its uploader, held outside Vue's
     * reactivity (it is the uploader's own state), and `upload`, the snapshot a
     * chip shows. The upload starts at once; the message is sent with the
     * uploaded file's uid, so the send never waits on bytes.
     */
    videoEntries(listName) {
      const lists = listName ? [LISTS[listName]] : Object.values(LISTS);
      return lists.flatMap((key) => this[key]).filter(isVideoEntry);
    },
    videoUpload(item) {
      return isVideoEntry(item) ? item.upload : null;
    },
    videoUidsOf(listName) {
      return this.videoEntries(listName)
        .filter((entry) => entry.upload.status === "ready" && entry.upload.fileUid)
        .map((entry) => entry.upload.fileUid);
    },
    composeVideoUids() {
      return this.videoUidsOf("compose");
    },
    quickReplyVideoUids() {
      return this.videoUidsOf("quickReply");
    },
    replyVideoUids() {
      return this.videoUidsOf("reply");
    },

    createVideoEntry(rawFile) {
      const identity = this.messagingIdentity || {};
      const uploader = markRaw(
        createVideoUploader({
          file: rawFile,
          uid: String(identity.subAccountNumber || ""),
          policy: toRaw(this.videoPolicy),
          domain: "message",
        })
      );
      const entry = reactive({
        isVideoEntry: true,
        name: rawFile.name,
        size: rawFile.size,
        type: rawFile.type,
        uploader,
        upload: uploader.getSnapshot(),
        // True once a send carried it: the message holds the video then, and
        // clearing the box must not discard it.
        sent: false,
      });
      uploader.subscribe((snapshot) => {
        entry.upload = snapshot;
      });
      uploader.start();
      return entry;
    },
    dropVideoEntry(entry) {
      Object.values(LISTS).forEach((key) => {
        const index = this[key].findIndex((item) => toRaw(item) === toRaw(entry));
        if (index >= 0) this[key].splice(index, 1);
      });
    },
    // An entry leaves its box: cancelled and discarded on keyon's side, unless a
    // message now carries it. Not awaited: the person has moved on.
    releaseVideoEntry(item) {
      if (!isVideoEntry(item)) return;
      if (item.sent) {
        item.uploader.release();
        return;
      }
      Promise.resolve(item.uploader.cancel()).catch(() => null);
    },
    discardAllVideoEntries() {
      this.videoEntries().forEach(this.releaseVideoEntry);
    },
    onVideoUploadAction(item, action) {
      if (!isVideoEntry(item)) return;
      if (action === "cancel") {
        this.dropVideoEntry(item);
        this.releaseVideoEntry(item);
      } else if (action === "verify") {
        item.uploader.verifyAgain();
      } else if (action === "retry") {
        item.uploader.retry();
      }
    },

    /**
     * Whether a send must wait: a video in the box that is not ready. A failed
     * upload counts, or the message would go out without the video the person
     * attached. `listName` is compose, quickReply or reply; none is all boxes.
     */
    uploadsBusy(listName) {
      return this.videoEntries(listName).some((entry) => isUploadBusy(entry.upload.status));
    },
    // Whether an upload is actually running (or parked): what a view change and the page-close prompt care about.
    uploadsInFlight() {
      return this.videoEntries().some((entry) => isUploadInFlight(entry.upload.status));
    },
    // A view change that would leave a running upload behind is refused, in words.
    viewChangeBlocked() {
      if (!this.uploadsInFlight()) return false;
      this.rejectAttachment("Video still uploading", VIDEO_UPLOADING_TEXT);
      return true;
    },
    armUnloadGuard(on) {
      if (typeof window === "undefined") return;
      if (on && !this.videoUnloadHandler) {
        this.videoUnloadHandler = warnBeforeUnload;
        window.addEventListener("beforeunload", warnBeforeUnload);
      } else if (!on && this.videoUnloadHandler) {
        window.removeEventListener("beforeunload", this.videoUnloadHandler);
        this.videoUnloadHandler = null;
      }
    },
    beginSend(name) {
      if (this.sendLocks[name]) return false;
      this.sendLocks[name] = true;
      return true;
    },
    endSend(name) {
      this.sendLocks[name] = false;
    },

    /**
     * After a send that answered Inserted: whether every video it named is on
     * the response's thread_attachments. If not (see VIDEO_NOT_ATTACHED_TEXT)
     * the sender is told, and the composer, its text and the uploaded video stay
     * for a second Send. If so the video is the message's from now on.
     *
     * A refusal also unticks Notify and drops its channel choices: the message
     * did go out, and keyon alerted its recipients when the send asked it to
     * (it notifies on every Inserted with notify_recipients), so the second Send
     * must not alert them again. Every send path returns as soon as this says
     * false, before its own Notify reset, so this is the one place that does it.
     * In a send-separately draft that stopped part way, the recipients not yet
     * sent to are not alerted on the second Send either unless the sender ticks
     * Notify again: it is one box for the whole draft.
     * @param {object} response the send's response body
     * @param {string[]} uids what the send named
     */
    verifyVideoAttached(response, uids) {
      if (!Array.isArray(uids) || !uids.length) return true;
      if (unattachedUids(response, uids).length) {
        this.rejectAttachment("Video not attached", VIDEO_NOT_ATTACHED_TEXT);
        // notifyRecipients is the widget's; the channel state is notifyChannelsComposeMixin's.
        this.notifyRecipients = false;
        this.resetNotifyChannelState();
        return false;
      }
      this.videoEntries().forEach((entry) => {
        if (uids.includes(entry.upload.fileUid)) entry.sent = true;
      });
      return true;
    },

    /*
     * Removing an attachment (keyon DeleteMessageAttachment). Offered only to the
     * message's sender; keyon refuses anyone else, and removes the file from
     * every message it went out with. The file is kept by keyon for retention,
     * not erased.
     */
    canRemoveAttachment(message, att) {
      const identity = this.messagingIdentity || {};
      return canDeleteMessageAttachment(message, att, identity.subAccountNumber);
    },
    openRemoveAttachment(message, att) {
      if (!this.canRemoveAttachment(message, att)) return;
      this.removeAttachmentTarget = { fileUid: att.file_uid, name: att.name || "attachment" };
      this.removeAttachmentError = "";
    },
    cancelRemoveAttachment() {
      if (this.removeAttachmentBusy) return;
      this.removeAttachmentTarget = null;
      this.removeAttachmentError = "";
    },
    async confirmRemoveAttachment(reason) {
      const target = this.removeAttachmentTarget;
      if (!target || this.removeAttachmentBusy) return;
      this.removeAttachmentBusy = true;
      this.removeAttachmentError = "";
      try {
        const r = await deleteMessageAttachment({ file_uid: target.fileUid, reason });
        if (!r.ok) {
          this.removeAttachmentError = r.message || "Could not remove the attachment.";
          return;
        }
        this.applyAttachmentRemoved(target.fileUid);
        this.removeAttachmentTarget = null;
      } finally {
        this.removeAttachmentBusy = false;
      }
    },
    /** Show the removal on every loaded message that carried the file, without a refetch. */
    applyAttachmentRemoved(fileUid) {
      this.messages = markAttachmentRemoved(this.messages, fileUid);
      this.filteredMessages = markAttachmentRemoved(this.filteredMessages, fileUid);
      const threads = [].concat(this.conversationThreads || [], this.filteredThreads || [], this.selectedThread ? [this.selectedThread] : []);
      threads.forEach((thread) => {
        if (!thread || typeof thread !== "object") return;
        if (Array.isArray(thread.messages)) thread.messages = markAttachmentRemoved(thread.messages, fileUid);
        if (thread.lastMessage) thread.lastMessage = markAttachmentRemoved([thread.lastMessage], fileUid)[0];
      });
      if (this.selectedMessage) this.selectedMessage = markAttachmentRemoved([this.selectedMessage], fileUid)[0];
    },

    // Keep a single "Re: " prefix so subjects don't grow (Re: Re: …).
    replySubject(subject, fallback = "Message") {
      const raw = String(subject || "").trim() || fallback;
      const withoutRe = raw.replace(/^(re:\s*)+/i, "").trim() || fallback;
      return `Re: ${withoutRe}`;
    },
  },
};
